import { isNumber } from 'lodash';
import {
  OrderActionDto,
  OrderDurationDto,
  OrderDurationNumberDto,
  OrderTypeDto,
  TradingBlockOrderActionNumberEnum,
  TradingBlockOrderDurationEnum,
  TradingBlockOrderStatusEnum,
  TradingBlockOrderTypeNumberEnum,
} from 'src/dtos';

export enum OrderTypeLabel {
  Market = 'Market Order',
  Limit = 'Limit Order',
  StopMarket = 'Stop Market Order',
  StopLimit = 'Stop Limit Order',
  MarketOnClose = 'Market on Close Order',
}

export enum OrderActionLabel {
  Buy = 'Buy',
  Sell = 'Sell',
}

export enum OrderDurationLabel {
  Day = 'End of Day',
  GTC = 'Good Until Cancelled',
}

export enum OrderStatusLabel {
  PROCESSING = 'PROCESSING',
  PENDING = 'PENDING',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}
export class OrderStatus {
  private _value: TradingBlockOrderStatusEnum;
  private _label: OrderStatusLabel;

  constructor(status?: TradingBlockOrderStatusEnum) {
    this._value = status ?? TradingBlockOrderStatusEnum.Undefined;
    this._label = this.findLabel();
  }

  private findLabel(): OrderStatusLabel {
    switch (this._value) {
      case TradingBlockOrderStatusEnum.New:
      case TradingBlockOrderStatusEnum.PartiallyFilled:
      case TradingBlockOrderStatusEnum.Calculated:
      case TradingBlockOrderStatusEnum.DoneForDay:
      case TradingBlockOrderStatusEnum.Saved:
      case TradingBlockOrderStatusEnum.Acknowledged:
        return OrderStatusLabel.PROCESSING;
      case TradingBlockOrderStatusEnum.PendingNew:
      case TradingBlockOrderStatusEnum.PendingCancel:
      case TradingBlockOrderStatusEnum.Scheduled:
      case TradingBlockOrderStatusEnum.CancelInitiated:
      case TradingBlockOrderStatusEnum.Stopped:
        return OrderStatusLabel.PENDING;
      case TradingBlockOrderStatusEnum.Rejected:
      case TradingBlockOrderStatusEnum.ReplaceRejected:
      case TradingBlockOrderStatusEnum.CancelRejected:
      case TradingBlockOrderStatusEnum.Suspended:
      case TradingBlockOrderStatusEnum.Expired:
        return OrderStatusLabel.REJECTED;
      case TradingBlockOrderStatusEnum.Filled:
        return OrderStatusLabel.COMPLETED;
      case TradingBlockOrderStatusEnum.Cancelled:
      case TradingBlockOrderStatusEnum.PartiallyCancelled:
        return OrderStatusLabel.CANCELLED;
      default:
        return OrderStatusLabel.PROCESSING;
    }
  }

  get value(): TradingBlockOrderStatusEnum {
    return this._value;
  }

  get label(): OrderStatusLabel {
    return this._label;
  }

  get isPending() {
    return this._label === OrderStatusLabel.PENDING;
  }

  get isCompleted() {
    return this._label === OrderStatusLabel.COMPLETED;
  }

  get isCancelled() {
    return this._label === OrderStatusLabel.CANCELLED;
  }

  get isPendingNew() {
    return this._value === TradingBlockOrderStatusEnum.PendingNew;
  }

  get isRejected() {
    return this._value === TradingBlockOrderStatusEnum.Rejected;
  }

  // TODO: introduce new order status Partially Cancelled
  get isPartiallyCancelled() {
    return this._value === TradingBlockOrderStatusEnum.PartiallyCancelled;
  }

  isCancellable() {
    return CANCEL_ORDER_STATUS_LIST.includes(this._value);
  }
}

export class OrderType {
  private _value: OrderTypeDto;
  private _label: OrderTypeLabel;

  constructor(type: TradingBlockOrderTypeNumberEnum | OrderTypeDto) {
    this._value = isNumber(type) ? this.mapTypeNumberToEnum(type) : (type as OrderTypeDto);
    this._label = this.mapTypeEnumToLabel(this._value);
  }

  private mapTypeNumberToEnum(type: TradingBlockOrderTypeNumberEnum): OrderTypeDto {
    switch (type) {
      case TradingBlockOrderTypeNumberEnum.Market:
        return OrderTypeDto.Market;
      case TradingBlockOrderTypeNumberEnum.Limit:
        return OrderTypeDto.Limit;
      case TradingBlockOrderTypeNumberEnum.Stop_Market:
        return OrderTypeDto.StopMarket;
      case TradingBlockOrderTypeNumberEnum.Stop_Limit:
        return OrderTypeDto.StopLimit;
      case TradingBlockOrderTypeNumberEnum.Market_On_Close:
        return OrderTypeDto.MarketOnClose;
      default:
        return OrderTypeDto.Market;
    }
  }

  private mapTypeEnumToLabel(type: OrderTypeDto): OrderTypeLabel {
    switch (type) {
      case OrderTypeDto.Market:
        return OrderTypeLabel.Market;
      case OrderTypeDto.Limit:
        return OrderTypeLabel.Limit;
      case OrderTypeDto.StopMarket:
        return OrderTypeLabel.StopMarket;
      case OrderTypeDto.StopLimit:
        return OrderTypeLabel.StopLimit;
      case OrderTypeDto.MarketOnClose:
        return OrderTypeLabel.MarketOnClose;
      default:
        return OrderTypeLabel.Market;
    }
  }

  get value(): OrderTypeDto {
    return this._value;
  }

  get label(): OrderTypeLabel {
    return this._label;
  }
}

export class OrderAction {
  private _value: OrderActionDto;
  private _label: OrderActionLabel;

  constructor(action?: TradingBlockOrderActionNumberEnum | OrderActionDto) {
    this._value = isNumber(action) ? this.mapActioNumberToEnum(action) : (action as OrderActionDto);
    this._label = this.findLabel();
  }

  private mapActioNumberToEnum(action: TradingBlockOrderActionNumberEnum): OrderActionDto {
    return action === TradingBlockOrderActionNumberEnum.Buy ? OrderActionDto.Buy : OrderActionDto.Sell;
  }

  private findLabel(): OrderActionLabel {
    return this._value === OrderActionDto.Buy ? OrderActionLabel.Buy : OrderActionLabel.Sell;
  }

  get value(): OrderActionDto {
    return this._value;
  }

  get label(): OrderActionLabel {
    return this._label;
  }

  get isBuy(): boolean {
    return this._value === OrderActionDto.Buy;
  }

  get isSell(): boolean {
    return this._value === OrderActionDto.Sell;
  }
}

export class OrderDuration {
  private _value: TradingBlockOrderDurationEnum;
  private _label: OrderDurationLabel;

  constructor(duration: TradingBlockOrderDurationEnum | OrderDurationNumberDto) {
    this._value = isNumber(duration) ? this.mapDurationNumberToEnum(duration) : duration;
    this._label = this.mapDurationEnumToLabel(this._value);
  }

  private mapDurationNumberToEnum(duration: OrderDurationNumberDto): TradingBlockOrderDurationEnum {
    return duration === OrderDurationNumberDto.Day
      ? TradingBlockOrderDurationEnum.Day
      : TradingBlockOrderDurationEnum.GTC;
  }

  private mapDurationEnumToLabel(duration: TradingBlockOrderDurationEnum): OrderDurationLabel {
    switch (duration) {
      case TradingBlockOrderDurationEnum.Day:
        return OrderDurationLabel.Day;
      case TradingBlockOrderDurationEnum.GTC:
        return OrderDurationLabel.GTC;
      default:
        return OrderDurationLabel.Day;
    }
  }

  get value(): TradingBlockOrderDurationEnum {
    return this._value;
  }

  get label(): OrderDurationLabel {
    return this._label;
  }
}

export interface Order {
  id: string;
  symbol: string;
  description: string;
  updatedAt: string;
  quantity: number;
  filledQuantity: number;
  isPartial: boolean;
  price: number;
  totalCost: number;
  type: OrderType;
  action: OrderAction;
  status: OrderStatus;
  duration: OrderDuration;
  commission: number;
  limit?: number;
  stop?: number;
}

// TODO: confirm pending order statuses
export const CANCEL_ORDER_STATUS_LIST = [
  TradingBlockOrderStatusEnum.New,
  TradingBlockOrderStatusEnum.PendingNew,
  TradingBlockOrderStatusEnum.PendingReplace,
  TradingBlockOrderStatusEnum.Scheduled,
  TradingBlockOrderStatusEnum.Initiated,
  TradingBlockOrderStatusEnum.ReplaceInitiated,
  TradingBlockOrderStatusEnum.PartiallyFilled,
];

export const OrderTypeNumberLabelMapper = new Map<TradingBlockOrderTypeNumberEnum, string>([
  [TradingBlockOrderTypeNumberEnum.Undefined, 'NA'],
  [TradingBlockOrderTypeNumberEnum.Market, 'Market Order'],
  [TradingBlockOrderTypeNumberEnum.Limit, 'Limit Order'],
  [TradingBlockOrderTypeNumberEnum.Stop_Market, 'Stop Market Order'],
  [TradingBlockOrderTypeNumberEnum.Stop_Limit, 'Stop Limit Order'],
  [TradingBlockOrderTypeNumberEnum.Market_On_Close, 'Market on Close Order'],
]);

export const OrderActionNumberLabelMapper = new Map<TradingBlockOrderActionNumberEnum, string>([
  [TradingBlockOrderActionNumberEnum.Undefined, 'NA'],
  [TradingBlockOrderActionNumberEnum.Buy, 'Buy'],
  [TradingBlockOrderActionNumberEnum.Sell, 'Sell'],
]);

export interface SecurityOrderView {
  type?: OrderTypeDto;
  action?: OrderActionDto;
  duration?: OrderDurationDto;
  quantity?: number;
  price?: number;
  stop?: number;
  estimatedTotal?: number;
}
